import { useStore } from '@nanostores/react';
import { memo, useEffect, useRef } from 'react';
import { $subAgentLogs, $activeSubAgentId, type SubAgentLog } from '~/lib/stores/sub-agent';
import { classNames } from '~/utils/classNames';

export const SubAgentPanel = memo(() => {
  const logs = useStore($subAgentLogs);
  const activeId = useStore($activeSubAgentId);
  const logEntries = Object.values(logs);

  const currentLog = activeId ? logs[activeId] : logEntries[logEntries.length - 1];

  if (logEntries.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 bg-surface border-b border-hairline px-3 py-2 text-[11.5px] text-muted">
          <span className="ui-caption">Sub-agents</span>
        </div>
        <div className="flex-1 flex items-center justify-center text-center px-6">
          <p className="text-[11.5px] text-muted max-w-[280px] leading-relaxed">
            No sub-agent activity yet. Sub-agents will appear here when they run.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Bar */}
      <div className="flex items-center gap-2 bg-surface border-b border-hairline px-3 py-2 text-[11.5px] text-muted">
        <span className="ui-caption">Sub-agents</span>
        <span className="text-secondary">· {logEntries.length} active</span>
      </div>

      {/* Agent tabs */}
      {logEntries.length > 1 && (
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-hairline overflow-x-auto bg-surface">
          {logEntries.map((log) => {
            const isActive = log.toolCallId === (currentLog?.toolCallId ?? '');
            return (
              <button
                key={log.toolCallId}
                onClick={() => $activeSubAgentId.set(log.toolCallId)}
                className={classNames('ui-tab whitespace-nowrap', isActive && 'ui-tab-active')}
              >
                {log.label}
                {log.status === 'running' && <span className="ml-1 i-svg-spinners:ring-resize text-[10px]" />}
                {log.status === 'complete' && <span className="ml-1 i-ph:check text-success text-[10px]" />}
                {log.status === 'error' && <span className="ml-1 i-ph:x text-danger text-[10px]" />}
              </button>
            );
          })}
        </div>
      )}

      {currentLog ? <AgentLogStream log={currentLog} /> : null}
    </div>
  );
});

const AgentLogStream = memo(({ log }: { log: SubAgentLog }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [log.steps.length, log.streamedText.length, log.summary]);

  const bodyText = log.status === 'running' ? log.streamedText : log.summary || log.streamedText;

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 font-geist-mono text-[11.5px] space-y-1 bg-page">
      <div className="flex items-center gap-2 pb-2 mb-2 border-b border-hairline">
        <span className="i-ph:git-branch text-accent" />
        <span className="text-primary font-geist text-[13px]">{log.label}</span>
        <StatusBadge status={log.status} />
      </div>

      {log.steps.map((step, i) => (
        <div key={i} className="flex items-start gap-2 py-0.5">
          <StepIcon toolName={step.toolName} status={step.status} />
          <span className={classNames('leading-relaxed', step.status === 'error' ? 'text-danger' : 'text-secondary')}>
            {step.message}
          </span>
        </div>
      ))}

      {log.status === 'running' && !log.streamedText && (
        <div className="flex items-center gap-2 py-1 text-muted">
          <span className="i-svg-spinners:ring-resize text-[11px]" />
          <span>Working…</span>
        </div>
      )}

      {bodyText && (
        <div className="mt-3 pt-3 border-t border-hairline">
          <div className="ui-caption mb-1.5 flex items-center gap-1.5">
            {log.status === 'running' ? 'Streaming' : 'Summary'}
            {log.status === 'running' && <span className="i-svg-spinners:ring-resize text-[10px]" />}
          </div>
          <pre className="text-secondary whitespace-pre-wrap leading-relaxed">{bodyText}</pre>
        </div>
      )}
    </div>
  );
});

function StepIcon({ toolName, status }: { toolName?: string; status: string }) {
  if (status === 'error') {
    return <span className="i-ph:x-circle text-danger text-[11px] flex-shrink-0 mt-0.5" />;
  }

  if (status === 'done') {
    return <span className="i-ph:check-circle text-success text-[11px] flex-shrink-0 mt-0.5" />;
  }

  const iconMap: Record<string, string> = {
    read_files: 'i-ph:file-text',
    list_files: 'i-ph:folder-open',
    glob: 'i-ph:file-search',
    search_code: 'i-ph:magnifying-glass',
    run_shell: 'i-ph:terminal',
    web_search: 'i-ph:globe',
    web_fetch: 'i-ph:download',
    lint_check: 'i-ph:check-circle',
    take_screenshot: 'i-ph:camera',
  };

  const icon = toolName ? iconMap[toolName] || 'i-ph:caret-right' : 'i-ph:caret-right';

  return <span className={`${icon} text-muted text-[11px] flex-shrink-0 mt-0.5`} />;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'running') {
    return (
      <span className="ml-auto ui-pill text-accent border-accent/30">
        <span className="i-svg-spinners:ring-resize text-[9px]" />
        Running
      </span>
    );
  }

  if (status === 'complete') {
    return (
      <span className="ml-auto ui-pill text-success border-success/30">
        <span className="i-ph:check text-[9px]" />
        Complete
      </span>
    );
  }

  return (
    <span className="ml-auto ui-pill text-danger border-danger/30">
      <span className="i-ph:x text-[9px]" />
      Error
    </span>
  );
}
